/**
 * 模块 C: T+0 交易参数模板预览器
 *
 * Phase A2 (2026-04-27) - 改造说明:
 * - 旧版: 单一表单写入 system_config (虚假"仅作文档"标签)
 * - 新版: 只读模板预览器, 单一数据源 (strategyTemplates.ts v4.0 via paramTemplates query)
 *
 * 架构:
 * - 引擎 tab: V1 配对 / V1 金字塔 / V2 VWAP+ATR
 * - 模板 sub-tab: 保守 / 均衡 / 积极 / 高频
 * - 参数分组: 方向一/方向二/通用 (V1) 或 入场出场/风控/通用 (V2)
 * - 顶部警告横幅: 引导用户去监控列表编辑
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertCircle, ChevronRight, Loader2, TrendingUp, TrendingDown, Shield, Settings2 } from "lucide-react";

// 引擎元信息
const ENGINES = [
  {
    id: "paired",
    name: "V1 配对模式",
    description: "回调/反弹信号 + 严格配对",
    color: "blue",
    riskBadge: "🟢 稳健",
    templateIds: ["conservative", "balanced", "aggressive", "highfreq"],
  },
  {
    id: "pyramid",
    name: "V1 金字塔",
    description: "趋势跟踪 + 分批加仓 + 一次性平仓",
    color: "amber",
    riskBadge: "🟡 进取",
    templateIds: ["pyramid_conservative", "pyramid_balanced", "pyramid_aggressive", "pyramid_highfreq"],
  },
  {
    id: "vwap_atr",
    name: "V2 VWAP+ATR",
    description: "VWAP 锚定 + ATR 自适应 + 连续同方向",
    color: "green",
    riskBadge: "🟢 推荐",
    templateIds: ["v2_conservative", "v2_balanced", "v2_aggressive", "v2_highfreq"],
  },
];

// 模板风格
const TEMPLATE_STYLES: Record<string, { label: string; color: string; emoji: string }> = {
  conservative: { label: "保守稳健", color: "text-blue-400", emoji: "🛡️" },
  balanced: { label: "均衡精选", color: "text-green-400", emoji: "⚖️" },
  aggressive: { label: "积极进取", color: "text-orange-400", emoji: "🔥" },
  highfreq: { label: "高频赚取", color: "text-purple-400", emoji: "⚡" },
};

// 参数分组定义 (V1)
const V1_GROUPS = [
  {
    id: "s1",
    title: "方向一: 追高回调 (先卖后买)",
    icon: TrendingUp,
    color: "text-gain",
    fields: [
      { key: "s1CallbackPct", label: "回调阈值", unit: "%", desc: "从波段最高点回调超过此值触发卖出基准" },
      { key: "s1ObserveWindowSec", label: "观察窗口", unit: "秒", desc: "观察价格不再创新高的时长" },
      { key: "s1ConfirmDropMaxPct", label: "确认期内允许跌幅上限", unit: "%", desc: "观察期内跌幅超此值认为趋势确立, 不再卖出" },
      { key: "s1BuybackDropPct", label: "止盈阈值 (买回)", unit: "%", desc: "卖出后价格再跌此值买回, 一轮完成" },
      { key: "s1StopLossRisePct", label: "止损阈值", unit: "%", desc: "卖出后价格反涨此值, 立即买入止损" },
      { key: "s1FillWaitSec", label: "委托超时", unit: "秒", desc: "限价单超时后市价补单" },
    ],
  },
  {
    id: "s2",
    title: "方向二: 探底反弹 (先买后卖)",
    icon: TrendingDown,
    color: "text-loss",
    fields: [
      { key: "s2BouncePct", label: "反弹阈值", unit: "%", desc: "从波段最低点反弹超过此值触发买入基准" },
      { key: "s2ObserveWindowSec", label: "观察窗口", unit: "秒", desc: "观察价格不再创新低的时长" },
      { key: "s2ConfirmRiseMinPct", label: "确认期内最小涨幅", unit: "%", desc: "观察期内涨幅低于此值认为反弹无力, 不买入" },
      { key: "s2SellRisePct", label: "止盈阈值 (卖出)", unit: "%", desc: "买入后价格再涨此值卖出, 一轮完成" },
      { key: "s2StopLossDropPct", label: "止损阈值", unit: "%", desc: "买入后价格反跌此值, 立即卖出止损" },
      { key: "s2FillWaitSec", label: "委托超时", unit: "秒", desc: "限价单超时后市价补单" },
    ],
  },
  {
    id: "common",
    title: "通用设置",
    icon: Settings2,
    color: "text-muted-foreground",
    fields: [
      { key: "tradeLotSize", label: "单笔下单股数", unit: "股", desc: "每笔交易的下单量, 100 的倍数" },
      { key: "maxDailyRounds", label: "每日最大轮次", unit: "轮", desc: "当日完成多少轮 T+0 后停止" },
      { key: "maxConsecutiveStopLoss", label: "连续止损上限", unit: "次", desc: "连续止损次数达此值触发熔断" },
      { key: "stopLossCooldownSeconds", label: "止损冷却", unit: "秒", desc: "止损后停止下单的冷却时长" },
      { key: "tailProtectMinutes", label: "尾盘保护", unit: "分钟", desc: "距收盘多少分钟后强制做平" },
      { key: "useL2Direction", label: "L2 主动单方向", unit: "", desc: "使用 L2 主动买卖单方向作为信号过滤 (1=启用, 0=关闭)" },
      { key: "hardDailyLossPct", label: "日亏损硬熔断", unit: "%", desc: "当日累计净亏损达持仓 X% 强制全天停 T+0" },
    ],
  },
];

// V2 参数分组
const V2_GROUPS = [
  {
    id: "entry",
    title: "入场 / 出场",
    icon: TrendingUp,
    color: "text-green-400",
    fields: [
      { key: "v2SellZoneValue", label: "卖出区偏离", unit: "× ATR", desc: "价格高于 VWAP+N×ATR 触发卖出区" },
      { key: "v2BuyZoneValue", label: "买入区偏离", unit: "× ATR", desc: "价格低于 VWAP-N×ATR 触发买入区" },
      { key: "v2TargetValue", label: "止盈回归", unit: "× ATR", desc: "价格回归 VWAP N×ATR 后止盈" },
      { key: "v2StopLossValue", label: "止损扩大", unit: "× ATR", desc: "偏离继续扩大 N×ATR 触发止损" },
      { key: "v2ConfirmSec", label: "信号确认时长", unit: "秒", desc: "信号持续 N 秒才执行 (抗插针)" },
      { key: "v2TimeoutSec", label: "等待平仓超时", unit: "秒", desc: "未达止盈/止损时主动平仓时间" },
    ],
  },
  {
    id: "risk",
    title: "风控 / 连续操作",
    icon: Shield,
    color: "text-orange-400",
    fields: [
      { key: "v2MaxDailyRounds", label: "每日最大轮次", unit: "轮", desc: "" },
      { key: "v2MaxConsecutive", label: "同方向连续上限", unit: "次", desc: "连续同方向操作达此值熔断, 防单边趋势爆仓" },
      { key: "maxConsecutiveStopLoss", label: "连续止损熔断", unit: "次", desc: "" },
      { key: "stopLossCooldownSeconds", label: "止损冷却", unit: "秒", desc: "" },
      { key: "v2MaxDailyLossValue", label: "日亏损上限", unit: "元", desc: "当日累计亏损达此值停止 T+0" },
      { key: "v2FlatDeadlineMin", label: "尾盘强平", unit: "分钟", desc: "距收盘多少分钟强制平仓" },
      { key: "v2MinDayRangeATR", label: "振幅过滤", unit: "× ATR", desc: "日内振幅低于此值不交易" },
    ],
  },
  {
    id: "common",
    title: "通用",
    icon: Settings2,
    color: "text-muted-foreground",
    fields: [
      { key: "tradeLotSize", label: "单笔下单股数", unit: "股", desc: "" },
      { key: "v2StepATR", label: "加仓阶梯", unit: "× ATR", desc: "金字塔加仓时每层间隔" },
      { key: "v2MaxExposure", label: "最大净敞口", unit: "股", desc: "金字塔多层后最大累计敞口" },
      { key: "v2EntryOrderStrategy", label: "入场挂单策略", unit: "", desc: "weighted_mid=加权中间价, counterparty=对手价" },
      { key: "v2ExitOrderStrategy", label: "出场挂单策略", unit: "", desc: "" },
      { key: "v2PartialTakeProfitRatio", label: "首次分批止盈比例", unit: "", desc: "首次达到 target 时平仓比例 (0.5=50%)" },
      { key: "v2TrailingStopATR", label: "Trailing 止损步长", unit: "× ATR", desc: "持仓后从极值价回撤 N×ATR 触发剩余平仓" },
    ],
  },
];

// 金字塔参数分组 (V1 paired 基础 + 金字塔特有字段)
const PYRAMID_GROUPS = [
  {
    id: "trigger",
    title: "入场触发 (沿用 V1 配对方向一/二)",
    icon: TrendingUp,
    color: "text-gain",
    fields: [
      { key: "s1CallbackPct", label: "方向一: 回调阈值", unit: "%", desc: "高点回调触发首笔卖出" },
      { key: "s1ObserveWindowSec", label: "方向一: 观察窗口", unit: "秒", desc: "" },
      { key: "s2BouncePct", label: "方向二: 反弹阈值", unit: "%", desc: "低点反弹触发首笔买入" },
      { key: "s2ObserveWindowSec", label: "方向二: 观察窗口", unit: "秒", desc: "" },
      { key: "pyramidAllowS2", label: "允许方向二加仓", unit: "0/1", desc: "0=禁用 (默认, 防爆仓), 1=启用" },
    ],
  },
  {
    id: "scale",
    title: "金字塔加仓",
    icon: TrendingUp,
    color: "text-amber-400",
    fields: [
      { key: "pyramidMaxLayers", label: "最大加仓层数", unit: "层", desc: "最多加仓多少次, 之后只观察不加仓" },
      { key: "pyramidAddThresholdPct", label: "加仓触发", unit: "%", desc: "趋势顺向继续偏离持仓均价此值, 触发加仓" },
      { key: "pyramidAddConfirmSec", label: "加仓确认", unit: "秒", desc: "信号持续 N 秒才加仓" },
      { key: "pyramidScaleMode", label: "加仓比例", unit: "", desc: "equal=等量, decrease=递减(类马丁), increase=递增" },
    ],
  },
  {
    id: "exit",
    title: "出场 / 风控",
    icon: Shield,
    color: "text-orange-400",
    fields: [
      { key: "pyramidPartialTakeProfitPct", label: "首批止盈比例", unit: "", desc: "趋势止盈时先平此比例 (0.5=50%)" },
      { key: "pyramidTrailingStopPct", label: "移动止损步长", unit: "%", desc: "首批止盈后从极值价回撤此值平剩余" },
      { key: "pyramidStopLossFromAvg", label: "硬止损 (相对均价)", unit: "%", desc: "持仓均价反向偏离此值强平所有" },
      { key: "maxConsecutiveStopLoss", label: "连续止损熔断", unit: "次", desc: "" },
      { key: "stopLossCooldownSeconds", label: "止损冷却", unit: "秒", desc: "" },
      { key: "tailProtectMinutes", label: "尾盘保护", unit: "分钟", desc: "" },
    ],
  },
  {
    id: "common",
    title: "通用",
    icon: Settings2,
    color: "text-muted-foreground",
    fields: [
      { key: "tradeLotSize", label: "单笔下单股数", unit: "股", desc: "" },
      { key: "maxDailyRounds", label: "每日最大轮次", unit: "轮", desc: "" },
      { key: "useL2Direction", label: "L2 主动单方向", unit: "0/1", desc: "" },
      { key: "hardDailyLossPct", label: "日亏损硬熔断", unit: "%", desc: "" },
    ],
  },
];


interface Template {
  id: string;
  name: string;
  description?: string;
  engine?: string;
  params: Record<string, any>;
}

export default function ModuleCTemplatePreview() {
  const { data: templates, isLoading } = trpc.agentMonitor.paramTemplates.useQuery();
  const [selectedEngine, setSelectedEngine] = useState<string>("paired");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("balanced");

  // 当前引擎的模板列表
  const currentEngineMeta = ENGINES.find(e => e.id === selectedEngine)!;
  const engineTemplates = useMemo(() => {
    if (!templates) return [];
    return (templates as Template[]).filter(t => {
      if (selectedEngine === "vwap_atr") return t.id?.startsWith("v2_");
      if (selectedEngine === "pyramid") return t.id?.startsWith("pyramid_");
      // V1 paired: 不带前缀
      return !t.id?.startsWith("v2_") && !t.id?.startsWith("pyramid_");
    });
  }, [templates, selectedEngine]);

  // 当前选中模板
  const currentTemplate = useMemo(() => {
    if (!engineTemplates.length) return null;
    // selectedTemplate 是基础名 (conservative/balanced/...)
    // 实际 id 可能带前缀 (v2_balanced, pyramid_balanced)
    const targetId = selectedEngine === "vwap_atr" ? `v2_${selectedTemplate}`
      : selectedEngine === "pyramid" ? `pyramid_${selectedTemplate}`
      : selectedTemplate;
    return engineTemplates.find(t => t.id === targetId) || engineTemplates[0];
  }, [engineTemplates, selectedTemplate, selectedEngine]);

  const groups = selectedEngine === "vwap_atr" ? V2_GROUPS
    : selectedEngine === "pyramid" ? PYRAMID_GROUPS
    : V1_GROUPS;
  const style = TEMPLATE_STYLES[selectedTemplate] || TEMPLATE_STYLES.balanced;

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          模块 C: T+0 交易参数模板预览器
          <Badge variant="outline" className="text-[9px] ml-1 border-green-500/50 text-green-400">v4.0 单一数据源</Badge>
        </CardTitle>
        <CardDescription className="text-xs space-y-1">
          <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30">
            <AlertCircle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-amber-300/90">
              <strong>本页只读</strong>。展示 8 套预设模板的"出厂默认参数"。
              真实参数在 <span className="text-amber-200">「Agent 监控列表」</span> 中按股票单独配置。
              <ChevronRight className="inline h-3 w-3" />
              个股参数页有 <span className="text-amber-200">「🔄 恢复初始值」</span> 按钮可重置回这些默认值。
            </div>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            加载模板中...
          </div>
        ) : (
          <>
            {/* 引擎选择 */}
            <div>
              <div className="text-[11px] text-muted-foreground mb-2">策略引擎</div>
              <Tabs value={selectedEngine} onValueChange={setSelectedEngine}>
                <TabsList className="grid grid-cols-3 w-full h-auto">
                  {ENGINES.map(e => (
                    <TabsTrigger key={e.id} value={e.id} className="flex-col py-2 h-auto">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-xs">{e.name}</span>
                        <span className="text-[9px]">{e.riskBadge}</span>
                      </div>
                      <div className="text-[9px] text-muted-foreground mt-0.5">{e.description}</div>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* 模板选择 */}
            <div>
              <div className="text-[11px] text-muted-foreground mb-2 flex items-center justify-between">
                <span>预设模板 ({currentEngineMeta.name})</span>
                {currentTemplate && (
                  <Badge variant="outline" className={`text-[9px] ${style.color}`}>
                    {style.emoji} {style.label}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {["conservative", "balanced", "aggressive", "highfreq"].map(tid => {
                  const s = TEMPLATE_STYLES[tid];
                  return (
                    <button
                      key={tid}
                      onClick={() => setSelectedTemplate(tid)}
                      className={`px-2 py-2 rounded border text-xs transition-colors ${
                        selectedTemplate === tid
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/30 bg-muted/20 text-muted-foreground hover:border-border/50"
                      }`}
                    >
                      <div>{s.emoji} {s.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 模板描述 */}
            {currentTemplate?.description && (
              <div className="text-[11px] text-muted-foreground bg-muted/20 rounded p-2 border border-border/30">
                {currentTemplate.description}
              </div>
            )}

            {/* 参数分组展示 */}
            {currentTemplate && (
              <Tabs defaultValue={groups[0].id}>
                <TabsList className={`grid w-full ${groups.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  {groups.map(g => {
                    const Icon = g.icon;
                    return (
                      <TabsTrigger key={g.id} value={g.id} className="text-xs">
                        <Icon className={`h-3 w-3 mr-1 ${g.color}`} />
                        {g.title.split(":")[0].split(" /")[0]}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                {groups.map(g => (
                  <TabsContent key={g.id} value={g.id} className="space-y-2 mt-3">
                    <div className="text-[11px] font-medium flex items-center gap-1 mb-2">
                      <g.icon className={`h-3.5 w-3.5 ${g.color}`} />
                      {g.title}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {g.fields.map(f => {
                        const v = currentTemplate.params?.[f.key];
                        const display = v === undefined || v === null ? "-" : (typeof v === "number" ? v.toString() : String(v));
                        return (
                          <div key={f.key} className="p-2 rounded bg-muted/10 border border-border/20">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-medium">{f.label}</span>
                              <span className="text-[12px] font-mono text-primary">
                                {display}{f.unit && <span className="text-[9px] text-muted-foreground ml-0.5">{f.unit}</span>}
                              </span>
                            </div>
                            {f.desc && (
                              <div className="text-[9px] text-muted-foreground/70 mt-1">{f.desc}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            )}

            {/* 底部说明 */}
            <div className="text-[10px] text-muted-foreground/60 pt-2 border-t border-border/30">
              💡 提示: 修改某只股票的参数请在 「Agent 监控列表」 → 选择股票 → 个股参数页面操作。
              所有 monitor 默认 addSource=manual, 即使切换 LIVE_TRADE 模式也需要在 Agent 列表二次授权。
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
